import { useState } from "react";
import { Shield, AlertTriangle, Gift } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";

/**
 * TopBar - A subtle banner showing payment info
 * Displays "Pago contraentrega" message with link to info modal
 */
export const TopBar = () => {
  const [paymentInfoOpen, setPaymentInfoOpen] = useState(false);

  return (
    <>
      {/* Subtle top bar - light/white with marquee animation */}
      <div className="bg-white/90 backdrop-blur-sm border-b border-gray-100 text-gray-600 text-center py-1 text-[11px] overflow-hidden">
        <button
          onClick={() => setPaymentInfoOpen(true)}
          className="inline-flex items-center gap-1.5 hover:text-amber-600 transition-colors animate-marquee"
          data-testid="topbar-payment-info"
        >
          <Shield size={11} className="text-amber-500 animate-pulse" />
          <span>Pago contraentrega: Revisas el producto y luego lo pagas</span>
          <span className="text-amber-500">→</span>
        </button>
      </div>

      {/* Payment Info Modal */}
      <PaymentInfoModal open={paymentInfoOpen} onOpenChange={setPaymentInfoOpen} />
    </>
  );
};

/**
 * PaymentInfoModal - Reusable modal with payment process details
 */
export const PaymentInfoModal = ({ open, onOpenChange }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0F172A]">
            <Shield size={20} className="text-[#D97706]" />
            Proceso de Compra Segura
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2 text-sm">
          {/* Item 1 */}
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#10B981] text-white flex items-center justify-center text-xs font-bold">1</div>
            <p className="text-[#374151]">
              <strong>Clueer no solicita pagos anticipados</strong> dentro de su marketplace a ningún usuario ni la implementación de datos bancarios, tarjetas o información sensible del mismo.
            </p>
          </div>

          {/* Item 2 */}
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#10B981] text-white flex items-center justify-center text-xs font-bold">2</div>
            <div className="text-[#374151]">
              <p>Una vez sea ganador de su producto coordinará con el vendedor la <strong>inspección física</strong> de su producto el cual debe concordar con el anuncio un 100%. De no ser así, no está obligado a pagarlo.</p>
              <p className="mt-2">Si está conforme con el producto podrá emitir su pago vía:</p>
              <div className="flex items-center gap-3 mt-2">
                <div className="flex items-center gap-1 bg-[#00D1A1]/10 px-3 py-1.5 rounded-full">
                  <span className="text-[#00D1A1] font-bold text-xs">Yappy</span>
                </div>
                <span className="text-[#64748B]">o</span>
                <div className="flex items-center gap-1 bg-[#003366]/10 px-3 py-1.5 rounded-full">
                  <span className="text-[#003366] font-semibold text-xs">BAC Panamá</span>
                </div>
              </div>
            </div>
          </div>

          {/* Item 3 */}
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#F59E0B] text-white flex items-center justify-center text-xs font-bold">3</div>
            <div className="text-[#374151]">
              <p><strong>Envíos:</strong> Si desea un envío, el pago del producto deberá emitirse contra la prueba de envío (videollamada, fotos y documentación de la empresa de envío).</p>
              <p className="mt-1 text-xs text-[#64748B] bg-[#FEF3C7] p-2 rounded">
                <AlertTriangle size={12} className="inline mr-1" />
                Siempre recomendamos evaluar la alternativa de un tercero que pueda retirar el producto físicamente.
              </p>
            </div>
          </div>

          {/* Item 4 */}
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#EF4444] text-white flex items-center justify-center text-xs font-bold">4</div>
            <p className="text-[#374151]">
              El precio del producto una vez acordado y/o ganado por el usuario <strong className="text-[#EF4444]">NO ES NEGOCIABLE</strong>. Deberá pagar el total aplicado en el sitio web.
            </p>
          </div>

          {/* Item 5 */}
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#64748B] text-white flex items-center justify-center text-xs font-bold">5</div>
            <p className="text-[#374151]">
              Una vez sea entregado un producto al usuario y el mismo dé fe de su conformidad, <strong>no hay devoluciones o reclamos</strong> que apliquen a este tipo de gestión de compraventa.
            </p>
          </div>

          {/* Item 6 */}
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#64748B] text-white flex items-center justify-center text-xs font-bold">6</div>
            <p className="text-[#374151]">
              <strong>No se ofrecen garantías</strong> por los equipos vendidos. Son en su gran mayoría equipos con detalles estéticos, semifuncionales o funcionales que son bien detallados en la descripción y fotografías para que cada usuario evalúe a su propio criterio.
            </p>
          </div>

          {/* Item 7 */}
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#10B981] text-white flex items-center justify-center text-xs font-bold">
              <Gift size={12} />
            </div>
            <div className="text-[#374151]">
              <p><strong className="text-[#10B981]">Recompensas:</strong> Se ofrecen recompensas por comentarios de su experiencia con el vendedor, la plataforma y el producto.</p>
              <p className="mt-1 text-xs text-[#64748B]">Las recompensas son únicas y aplican solamente para usuarios que hayan realizado una compra. Se pagan vía Yappy al número designado en su formulario.</p>
            </div>
          </div>

          {/* Footer */}
          <div className="pt-4 border-t border-[#E2E8F0] text-center">
            <p className="font-bold text-[#0F172A]">Clueer | Marketplace de Subastas 🇵🇦</p>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} className="w-full rounded-full bg-[#0F172A] hover:bg-[#1e293b]">
            Entendido
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TopBar;
